import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import anime from "animejs";
import { StatusIcon } from "./StatusIcon";
import { RotateCw, ArrowRight } from "lucide-react";
import type { ProcessingItemData } from "./types";

interface ProcessingItemProps {
  item: ProcessingItemData;
  index: number;
  onRetry?: () => void;
}

export function ProcessingItem({ item, index, onRetry }: ProcessingItemProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const stepTextRef = useRef<HTMLSpanElement>(null);
  const [prevStep, setPrevStep] = useState(item.extractionStep);
  const enteredRef = useRef(false);
  const glowedRef = useRef(false);

  // Stagger entrance
  useEffect(() => {
    const el = containerRef.current;
    if (!el || enteredRef.current) return;
    enteredRef.current = true;

    el.style.opacity = "0";
    el.style.transform = "translateY(12px)";

    anime({
      targets: el,
      opacity: [0, 1],
      translateY: [12, 0],
      easing: "easeOutCubic",
      duration: 400,
      delay: 200 + index * 50,
      complete: () => {
        el.style.willChange = "auto";
      },
    });
  }, [index]);

  // Step text transition
  useEffect(() => {
    if (item.extractionStep === prevStep) return;

    const el = stepTextRef.current;
    if (!el) {
      setPrevStep(item.extractionStep);
      return;
    }

    // Slide out old text
    anime({
      targets: el,
      translateX: [0, -20],
      opacity: [1, 0],
      easing: "easeInQuart",
      duration: 150,
      complete: () => {
        setPrevStep(item.extractionStep);
        // Slide in new text
        anime({
          targets: el,
          translateX: [20, 0],
          opacity: [0, 1],
          easing: "easeOutCubic",
          duration: 200,
        });
      },
    });
  }, [item.extractionStep, prevStep]);

  // Border glow on extraction complete
  useEffect(() => {
    if (item.status !== "extracted" || glowedRef.current) return;
    glowedRef.current = true;

    const el = containerRef.current;
    if (!el) return;

    anime({
      targets: el,
      boxShadow: [
        "0 0 0 0 color-mix(in oklab, var(--tr-ok) 0%, transparent)",
        "0 0 12px 2px color-mix(in oklab, var(--tr-ok) 25%, transparent)",
        "0 0 0 0 color-mix(in oklab, var(--tr-ok) 0%, transparent)",
      ],
      easing: "easeOutCubic",
      duration: 800,
    });
  }, [item.status]);

  const borderClass =
    item.status === "extracted"
      ? "border-ok/30"
      : item.status === "failed" || item.status === "error"
        ? "border-bad/30"
        : item.status === "extracting"
          ? "border-info/30"
          : "border-line";

  const failedBg =
    item.status === "failed" || item.status === "error"
      ? { background: "color-mix(in oklab, var(--tr-bad) 6%, var(--tr-panel))" }
      : undefined;

  const displayName = item.result?.artist_name ?? item.artistName;

  return (
    <div
      ref={containerRef}
      className={`flex items-center gap-3 rounded-ds-sm border bg-panel px-4 py-3 transition-colors ${borderClass}`}
      style={{ willChange: "transform, opacity", ...failedBg }}
    >
      <StatusIcon
        status={item.status === "staged" ? "uploading" : item.status}
      />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-ink-2">{displayName}</p>

        {item.status === "uploading" && (
          <p className="text-xs text-ink-3">Uploading…</p>
        )}

        {item.status === "extracting" && (
          <span
            ref={stepTextRef}
            className="inline-block text-xs text-info"
            style={{ willChange: "transform, opacity" }}
          >
            {prevStep || "Extracting data from PDF…"}
          </span>
        )}

        {item.status === "extracted" && (
          <p className="text-xs text-ok">Extraction complete</p>
        )}

        {(item.status === "failed" || item.status === "error") && (
          <p className="text-xs text-bad">{item.error || "Failed"}</p>
        )}

        {item.fileName && (
          <p className="mt-0.5 truncate font-mono text-[11px] text-ink-4">
            {item.fileName}
          </p>
        )}
      </div>

      {/* Right side actions */}
      {item.status === "extracted" && item.result && (
        <Link
          to={`/riders/${item.result.rider_id}/review`}
          className="btn btn--accent btn--sm shrink-0"
        >
          Review
          <ArrowRight className="h-3 w-3" strokeWidth={1.6} />
        </Link>
      )}

      {(item.status === "failed" || item.status === "error") && onRetry && (
        <button
          onClick={onRetry}
          className="flex shrink-0 items-center gap-1 rounded-ds-xs px-2 py-1 text-xs text-bad transition-colors hover:bg-bad/10"
        >
          <RotateCw className="h-3 w-3" strokeWidth={1.6} />
          Retry
        </button>
      )}
    </div>
  );
}
